import {
  Box,
  SvgIconTypeMap,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { StatisticCardData } from '../../types/interfaces/statistic-card-data';
import { useTranslation } from 'react-i18next';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { OverridableComponent } from '@mui/types';
import { CalendarIcon } from 'src/icons/calendar';
import { ThumbIcon } from 'src/icons/thumb';
import { QuestionMarkIcon } from 'src/icons/question-mark';
import { FinishedIcon } from 'src/icons/finish';
import { ClockIcon } from 'src/icons/clock';
import { CallendarCancelledIcon } from 'src/icons/callendar-cancelled';

export default function StatisticCard({
  type,
  count,
  sx,
}: StatisticCardData & { sx?: SxProps }) {
  const { t } = useTranslation(['statistics', 'common']);

  const {
    palette: { mode },
  } = useTheme();

  let Icon: OverridableComponent<SvgIconTypeMap<object, 'svg'>> | null = null;
  let bgColor: string = '';
  let title = '';

  switch (type) {
    case 'TOTAL': {
      Icon = CalendarIcon;
      bgColor = mode === 'light' ? '#FDEFE0' : '#47301F';
      title = t('common.statisticCard.totalBookings');
      break;
    }

    case 'CONFIRMED': {
      Icon = ThumbIcon;
      bgColor = mode === 'light' ? '#E6F7EB' : '#234121';
      title = t('common.statisticCard.confirmed');
      break;
    }

    case 'UNCONFIRMED': {
      Icon = QuestionMarkIcon;
      bgColor = mode === 'light' ? '#EFEFEF' : '#434040';
      title = t('common.statisticCard.unconfirmed');
      break;
    }

    case 'FINISHED': {
      Icon = FinishedIcon;
      bgColor = mode === 'light' ? '#F4DFFC' : '#481D53';
      title = t('common.statisticCard.finished');
      break;
    }

    case 'FINISHED_EARLY': {
      Icon = ClockIcon;
      bgColor = mode === 'light' ? '#DFEBFC' : '#2C324E';
      title = t('common.statisticCard.finishedEarly');
      break;
    }

    case 'CANCELLED': {
      Icon = CallendarCancelledIcon;
      bgColor = mode === 'light' ? '#FFEEEC' : '#562E2E';
      title = t('common.statisticCard.cancelled');
    }
  }

  return (
    <CardWithContent
      cardSx={{
        minWidth: 'fit-content',
        borderColor: 'blue',
        mb: 0,
        ...sx,
      }}
      cardContentSx={{
        height: 'fit-content',
        paddingY: 1.25,
        borderColor: 'red',
        '&:last-child': { pb: 1.25 },
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          gap: 1.25,
        }}>
        <Box
          sx={{
            padding: 1.5,
            borderRadius: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${bgColor}`,
          }}>
          {Icon && <Icon sx={{ fontSize: 14 }} />}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
          }}>
          <Typography
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              textWrap: 'nowrap',
              letterSpacing: '0.15px',
              fontWeight: 'medium',
              fontSize: '10px',
              mb: 0.5,
              display: 'block',
            }}>
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{ fontWeight: 'medium', textWrap: 'nowrap' }}>
            {count}
          </Typography>
        </Box>
      </Box>
    </CardWithContent>
  );
}
